package com.gsb.vyapar.bhagya.interactor.util

import androidx.compose.runtime.snapshots.SnapshotStateMap
import androidx.compose.ui.graphics.ImageBitmap
import com.gsb.vyapar.bhagya.datasource.cache.SqlDelightClient
import com.gsb.vyapar.bhagya.datasource.network.KtorClient
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.getState
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.launchDefault
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.updateState
import com.gsb.vyapar.bhagya.toComposeImageBitmap
import com.gsb.vyapar.bhagya.util.getMessage
import korlibs.image.bitmap.Bitmap
import korlibs.image.bitmap.resized
import korlibs.image.format.JPEG
import korlibs.image.format.readBitmap
import korlibs.io.file.VfsFile
import korlibs.io.file.std.MemoryVfs
import korlibs.math.geom.Anchor
import korlibs.math.geom.ScaleMode
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlin.math.sqrt

val isRequestInProgressMap = MutableStateFlow<Map<String, Boolean>>(emptyMap())
fun checkImageCache(url: String) {
    launchDefault {
        try {
            var image = getState().imageMap[url]
            if (image == null) {
                image = SqlDelightClient.getImage(url)?.toComposeImageBitmap()
                if (image == null) {
                    if (isRequestInProgressMap.value[url] != true) {
                        isRequestInProgressMap.update { it + (url to true) }
                        val imageByteArray = KtorClient.downloadImage(url).getOrNull()
                        image = imageByteArray?.toComposeImageBitmap()
                        if (image != null) {
                            updateState(getState().copy(imageMap = getNewImageMap(url, image)))
                            if (imageByteArray != null) {
                                SqlDelightClient.putImage(url, imageByteArray)
                            }
                        }
                        isRequestInProgressMap.update { it + (url to false) }
                    }
                } else {
                    updateState(getState().copy(imageMap = getNewImageMap(url, image)))
                }
            }
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }
}

fun checkImageCacheCallback(url: String, onBitmap: (ImageBitmap?) -> Unit) {
    launchDefault {
        try {
            var image = getState().imageMap[url]
            if (image == null) {
                image = SqlDelightClient.getImage(url)?.toComposeImageBitmap()
                if (image == null) {
                    if (isRequestInProgressMap.value[url] != true) {
                        isRequestInProgressMap.update { it + (url to true) }
                        val imageByteArray = KtorClient.downloadImage(url).getOrNull()
                        image = imageByteArray?.toComposeImageBitmap()
                        if (image != null) {
                            updateState(getState().copy(imageMap = getNewImageMap(url, image)))
                            if (imageByteArray != null) {
                                SqlDelightClient.putImage(url, imageByteArray)
                            }
                        }
                        isRequestInProgressMap.update { it + (url to false) }
                    }
                } else {
                    updateState(getState().copy(imageMap = getNewImageMap(url, image)))
                }
            }
            onBitmap(image)
        } catch (e: Exception) {
            e.printStackTrace()
            onBitmap(null)
        }
    }
}

fun getNewImageMap(url: String, image: ImageBitmap): SnapshotStateMap<String, ImageBitmap> {
    val map = getState().imageMap
    map[url] = image
    return map
}

fun uploadImageOld(data: ByteArray, fileName: String, imageUrl: (String) -> Unit) {
    if (isImageLessThan1MB(data)) {
        showLoading()
        launchDefault {
            val result = KtorClient.uploadImage(data, sanitizeFilename(fileName))
            hideLoading()
            if (result.isSuccess) {
                val imageUploadResponse = result.getOrNull()
                imageUploadResponse?.data?.url?.let {
                    imageUrl(it)
                    showSuccess("Image Uploaded")
                }?: run {
                    showError("There was a problem while uploading image")
                    hideLoading()
                }
            } else {
                showError("There was a problem while uploading image ${result.exceptionOrNull()?.getMessage()}")
                hideLoading()
            }
        }
    } else {
        showError("Image size should be less than 1 MB")
        hideLoading()
    }
}

fun uploadImage(data: ByteArray, fileName: String, imageUrl: (String) -> Unit) {
    if (isImageLessThan1MB(data)) {
        showLoading()
        launchDefault {
            val result = KtorClient.uploadGithubImage(data)
            hideLoading()
            if (result.isSuccess) {
                val githubUploadResponse = result.getOrNull()
                githubUploadResponse?.content?.downloadUrl?.let {
                    imageUrl(it)
                    showSuccess("Image Uploaded")
                }?: run {
                    showError("There was a problem while uploading image")
                    hideLoading()
                }
            } else {
                showError("There was a problem while uploading image ${result.exceptionOrNull()?.getMessage()}")
                hideLoading()
            }
        }
    } else {
        showError("Image size should be less than 1 MB")
        hideLoading()
    }
}

fun isImageLessThan1MB(imageData: ByteArray): Boolean {
    val maxSizeInBytes = 1024 * 1024 // 1 MB in bytes
    return imageData.size < maxSizeInBytes
}

fun sanitizeFilename(filename: String): String {
    val lastDotIndex = filename.lastIndexOf('.')

    return if (lastDotIndex != -1) {
        val namePart = filename.substring(0, lastDotIndex).replace("[^a-zA-Z0-9]".toRegex(), "")
        val extension = filename.substring(lastDotIndex)
        "$namePart$extension"
    } else {
        filename.replace("[^a-zA-Z0-9]".toRegex(), "")
    }
}

suspend fun resizeImage(byteArray: ByteArray, targetSizeBytes: Long = 150_000): ByteArray {
    if (byteArray.size < targetSizeBytes) return byteArray
    val vfsFile = getVfsFile(byteArray)
    val size = getImageSize(vfsFile)
    val originalPixels = size.first * size.second
    val originalSizeBytes = byteArray.size.toLong()
    val targetPixels = (targetSizeBytes.toDouble() / originalSizeBytes * originalPixels).toInt()
    val aspectRatio = size.first.toDouble() / size.second
    val targetWidth = sqrt(targetPixels * aspectRatio).toInt()
    val targetHeight = (targetWidth / aspectRatio).toInt()
    val originalBitmap = vfsFile.readBitmap().toBMP32IfRequired()
    var lowWidth = 50
    var highWidth = targetWidth
    var finalBitmap: Bitmap? = null
    var finalBytes: ByteArray? = null
    while (lowWidth <= highWidth) {
        val midWidth = (lowWidth + highWidth) / 2
        val midHeight = (midWidth / aspectRatio).toInt()
        val resizedBitmap = originalBitmap.resized(midWidth, midHeight, ScaleMode.FIT, Anchor.CENTER, false)
        val outputBytes = JPEG.encode(resizedBitmap)
        val currentSize = outputBytes.size.toLong()
        if (currentSize in (targetSizeBytes * 0.9).toLong()..targetSizeBytes) {
            finalBitmap = resizedBitmap
            finalBytes = outputBytes
            break
        } else if (currentSize > targetSizeBytes) {
            highWidth = midWidth - 1
        } else {
            lowWidth = midWidth + 1
            finalBitmap = resizedBitmap
            finalBytes = outputBytes
        }
    }
    return finalBytes ?: JPEG.encode(finalBitmap!!)
}

suspend fun rotateLeft(byteArray: ByteArray): ByteArray {
    val vfsFile = getVfsFile(byteArray)
    val bitmap = vfsFile.readBitmap()
    val rotatedBitmap = bitmap.rotatedLeft()
    return JPEG.encode(rotatedBitmap)
}

suspend fun rotateRight(byteArray: ByteArray): ByteArray {
    val vfsFile = getVfsFile(byteArray)
    val bitmap = vfsFile.readBitmap()
    val rotatedBitmap = bitmap.rotatedRight()
    return JPEG.encode(rotatedBitmap)
}

suspend fun getImageSize(vfsFile: VfsFile): Pair<Int, Int> {
    val bitmap: Bitmap = vfsFile.readBitmap()
    return Pair(bitmap.width, bitmap.height)
}

suspend fun getVfsFile(byteArray: ByteArray): VfsFile {
    return MemoryVfs()["tempImage"].apply { write(byteArray) }
}